// src/pages/customers/CustomerForm.tsx
import SelectDropdown from '@/components/common/SelectDropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { currencies } from '@/lib/constants/common';
import { customerType, gstTreatmentOptions, gstTypesOptions } from '@/lib/constants/customors';
import { customerSchema } from '@/lib/schemas/customer.schema';
import { customersAPI } from '@/lib/utils/api';
import { CustomerFormData } from '@/types/customer.types';
import { Form, Formik } from 'formik';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Initial Values
const initialValues: CustomerFormData = {
  customerType: 'individual',
  primaryContact: '',
  companyName: '',
  currency: 'INR',
  email: '',
  phone: '',
  customerAddress: {
    attention: '',
    countryRegion: 'India',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    remarks: '',
  },
  gstTreatment: {
    gstType: 'unregistered',
    taxPreference: 'taxable',
    gstNumber: '',
  },
};

// Component
const CustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [formData, setFormData] = useState<CustomerFormData>(initialValues);

  // Fetch Customer (Edit)
  useEffect(() => {
    if (!isEditing) return;

    const fetchCustomer = async () => {
      try {
        const res = await customersAPI.getById(id as string);
        const customer = res.data;

        setFormData({
          ...initialValues,
          ...customer,
          customerAddress: {
            ...initialValues.customerAddress,
            ...customer.customerAddress,
          },
          gstTreatment: {
            ...initialValues.gstTreatment,
            ...customer.gstTreatment,
          },
        });
      } catch {
        toast.error('Failed to fetch customer');
        navigate('/customers');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchCustomer();
  }, [id, isEditing, navigate]);

  // Submit
  const handleSubmit = async (values: any) => {
    setLoading(true);

    try {
      isEditing
        ? await customersAPI.update(id as string, values)
        : await customersAPI.create(values);

      toast.success(`Customer ${isEditing ? 'updated' : 'created'} successfully`);
      navigate('/customers');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      <div className="flex items-center gap-2">
        <Button
          variant={'ghost'}
          onClick={() => navigate('/customers')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Edit Customer' : 'Add Customer'}</h1>
      </div>

      <div className="bg-background rounded-lg shadow p-6">
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={customerSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form className="space-y-6">
              {/* ================= BASIC INFO ================= */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Customer Type *</Label>
                  <div className="space-y-1">
                    <SelectDropdown
                      value={values.customerType}
                      options={customerType}
                      onChange={(val) => {
                        setFieldValue('customerType', val);
                      }}
                    />
                    {touched.customerType && errors.customerType && (
                      <p className="text-sm text-red-500">{errors.customerType}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Primary Contact *</Label>
                  <div className="space-y-1">
                    <Input
                      name="primaryContact"
                      value={values.primaryContact}
                      placeholder="Enter Primary Contact"
                      onChange={handleChange}
                    />
                    {touched.primaryContact && errors.primaryContact && (
                      <p className="text-sm text-red-500">{errors.primaryContact}</p>
                    )}
                  </div>
                </div>

                {values.customerType === 'business' && (
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <div className="space-y-1">
                      <Input
                        name="companyName"
                        value={values.companyName}
                        placeholder="Enter Company Name"
                        onChange={handleChange}
                      />
                      {touched.companyName && errors.companyName && (
                        <p className="text-sm text-red-500">{errors.companyName}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Currency</Label>
                  <SelectDropdown
                    value={values.currency}
                    options={currencies}
                    onChange={(val) => {
                      setFieldValue('currency', val);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="space-y-1">
                    <Input
                      type="email"
                      name="email"
                      placeholder="example@email.com"
                      value={values.email}
                      onChange={handleChange}
                    />
                    {touched.email && errors.email && (
                      <p className="text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <div className="space-y-1">
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Enter Phone Number"
                      value={values.phone}
                      onChange={handleChange}
                    />
                    {touched.phone && errors.phone && (
                      <p className="text-sm text-red-500">{errors.phone}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator orientation="horizontal" className="!my-6" />

              {/* ================= ADDRESS ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">Address Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Attention</Label>
                    <Input
                      name="customerAddress.attention"
                      value={values.customerAddress.attention}
                      onChange={handleChange}
                      placeholder="Enter Attention"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Country/Region</Label>
                    <Input
                      name="customerAddress.countryRegion"
                      value={values.customerAddress.countryRegion}
                      onChange={handleChange}
                      placeholder="Enter Country/Region"
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label>Address</Label>
                    <Textarea
                      name="customerAddress.address"
                      value={values.customerAddress.address}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Enter Address"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      name="customerAddress.city"
                      value={values.customerAddress.city}
                      onChange={handleChange}
                      placeholder="Enter City"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      name="customerAddress.state"
                      value={values.customerAddress.state}
                      onChange={handleChange}
                      placeholder="Enter State"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Pin Code</Label>
                    <Input
                      name="customerAddress.pinCode"
                      value={values.customerAddress.pinCode}
                      onChange={handleChange}
                      placeholder="Enter Pin Code"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Remarks</Label>
                    <Input
                      name="customerAddress.remarks"
                      value={values.customerAddress.remarks}
                      onChange={handleChange}
                      placeholder="Enter Remarks"
                    />
                  </div>
                </div>
              </div>

              <Separator orientation="horizontal" className="!my-6" />

              {/* ================= GST INFO ================= */}
              <div>
                <h3 className="text-lg font-medium mb-4">GST Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>GST Type</Label>
                    <SelectDropdown
                      value={values.gstTreatment.gstType}
                      options={gstTypesOptions}
                      onChange={(val) => {
                        setFieldValue('gstTreatment.gstType', val);
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Tax Preference</Label>
                    <SelectDropdown
                      value={values.gstTreatment.taxPreference}
                      options={gstTreatmentOptions}
                      onChange={(val) => {
                        setFieldValue('gstTreatment.taxPreference', val);
                      }}
                    />
                  </div>

                  {values.gstTreatment.gstType === 'registered' && (
                    <div className="md:col-span-2 space-y-1">
                      <Label>GST Number *</Label>
                      <div className="space-y-1">
                        <Input
                          name="gstTreatment.gstNumber"
                          value={values.gstTreatment.gstNumber}
                          onChange={handleChange}
                          placeholder="22AAAAA0000A1Z5"
                        />
                        {touched.gstTreatment?.gstNumber && errors.gstTreatment?.gstNumber && (
                          <p className="text-sm text-red-500">{errors.gstTreatment.gstNumber}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ================= ACTIONS ================= */}
              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => navigate('/customers')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : isEditing ? 'Update Customer' : 'Create Customer'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CustomerForm;
